<template>
  <Modal :small="true">
    <div
      class="saas-template-detail__head saas-template-detail__head--modal margin-bottom-0"
    >
      <div class="saas-template-detail__icon">
        <i :class="template.icon"></i>
      </div>
      <div class="saas-template-detail__title">
        <div class="saas-template-detail__title-name">
          {{ template.name }}
        </div>
      </div>
    </div>
    <template v-if="page === 'select'">
      <div
        v-if="workspacesLoading"
        class="loading margin-top-2 margin-bottom-2"
      ></div>
      <template v-else>
        <p class="margin-top-3 margin-bottom-3">
          To start using the template {{ template.name }}, you first need to
          choose a workspace to where the template will be installed into.
          Optionally it is also possible to create a new workspace.
        </p>
        <Error :error="error"></Error>
        <div class="control">
          <label class="control__label">Select workspace</label>
          <div class="control__elements">
            <Dropdown v-model="selectedWorkspace">
              <DropdownItem
                v-for="workspace in workspaces"
                :key="workspace.id"
                :name="workspace.name"
                :value="workspace.id"
              ></DropdownItem>
              <DropdownItem
                name="Create new workspace"
                :value="0"
              ></DropdownItem>
            </Dropdown>
          </div>
        </div>
        <div class="actions actions--right">
          <Button
            :loading="installing"
            :disabled="installing"
            @click="install(template, selectedWorkspace)"
          >
            Create {{ template.name }}
          </Button>
        </div>
      </template>
    </template>
    <template v-else-if="page === 'login'">
      <p class="margin-top-3 margin-bottom-3">
        After signing in, you can choose a workspace where the template
        {{ template.name }} will be installed into.
      </p>
      <Login
        :display-header="false"
        :original="`/templates/${template.slug}#use`"
        :redirect-on-success="false"
        :login-buttons-compact="true"
        @success="loginSuccess"
      >
        <template #signup>
          {{ $t('login.signUpText') }}
          <a @click="page = 'register'">{{ $t('login.signUp') }}</a>
        </template>
      </Login>
    </template>
    <template v-else-if="page === 'register'">
      <p class="margin-top-3 margin-bottom-3">
        The {{ template.name }} template will be installed after creating your
        account.
      </p>
      <PasswordRegister @success="registerSuccess">
        <LoginButtons
          class="mb-32"
          show-border="top"
          :hide-if-no-buttons="true"
          :original="`/templates/${template.slug}#use`"
        />
        <LoginActions :original="`/templates/${template.slug}#use`">
          <li class="auth__action-link">
            <a @click="page = 'login'">I already have an account</a>
          </li>
        </LoginActions>
      </PasswordRegister>
    </template>
    <template v-else>
      <p class="margin-top-3 margin-bottom-3">
        In order to use the {{ template.name }} template, you need to have a
        Baserow account. Do you already have an account?
      </p>
      <div class="saas-template-detail__authenticate">
        <a
          class="saas-template-detail__authenticate-item"
          @click="page = 'login'"
        >
          <div class="saas-template-detail__authenticate-title">
            Sign in
            <i
              class="iconoir-log-in saas-template-detail__authenticate-icon"
            ></i>
          </div>
          <div class="saas-template-detail__authenticate-description">
            I have an account
          </div>
        </a>
        <a
          class="saas-template-detail__authenticate-item"
          @click="page = 'register'"
        >
          <div class="saas-template-detail__authenticate-title">
            Sign up
            <i
              class="iconoir-add-user saas-template-detail__authenticate-icon"
            ></i>
          </div>
          <div class="saas-template-detail__authenticate-description">
            I don't have an account
          </div>
        </a>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapGetters } from 'vuex'

import modal from '@baserow/modules/core/mixins/modal'
import error from '@baserow/modules/core/mixins/error'
import TemplateService from '@baserow/modules/core/services/template'
import PasswordRegister from '@baserow/modules/core/components/auth/PasswordRegister'
import Login from '@baserow/modules/core/components/auth/Login'
import LoginButtons from '@baserow/modules/core/components/auth/LoginButtons'
import LoginActions from '@baserow/modules/core/components/auth/LoginActions'
import SubscriptionService from '@saas/services/subscriptions'

export default {
  name: 'UseTemplateModal',
  components: {
    PasswordRegister,
    Login,
    LoginButtons,
    LoginActions,
  },
  mixins: [error, modal],
  props: {
    template: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      page: '',
      selectedWorkspace: 0,
      installing: false,
    }
  },
  async fetch() {
    await this.$store.dispatch('authProvider/fetchLoginOptions')
  },
  computed: {
    ...mapGetters({
      authenticated: 'auth/isAuthenticated',
      workspaces: 'workspace/getAll',
      workspacesLoading: 'workspace/isLoading',
      workspacesLoaded: 'workspace/isLoaded',
    }),
  },
  watch: {
    authenticated(value) {
      if (value && !this.workspacesLoaded) {
        this.$store.dispatch('workspace/loadAll')
      }
    },
  },
  methods: {
    async show(...args) {
      modal.methods.show.call(this, ...args)
      if (this.authenticated) {
        this.page = 'select'
        await this.fetchWorkspaces()
      } else {
        this.page = ''
      }
    },
    async loginSuccess() {
      await this.fetchWorkspaces()
      this.page = 'select'
    },
    async registerSuccess() {
      await this.fetchWorkspaces()
      this.page = 'select'
    },
    async fetchWorkspaces() {
      if (this.authenticated && !this.workspacesLoaded) {
        await this.$store.dispatch('workspace/fetchAll')
      }
      if (this.workspaces.length > 0) {
        this.selectedWorkspace = this.workspaces[0].id
      }
    },
    async install(template, workspaceId) {
      this.installing = true

      // If the user wants to create a new workspace, then we will first create a new workspace
      // having the name of the template.
      if (workspaceId === 0) {
        try {
          const workspace = await this.$store.dispatch('workspace/create', {
            name: template.name,
          })
          workspaceId = workspace.id

          await SubscriptionService(this.$client).startTrial(workspaceId)
          // This fetches the active licenses again, so that the user immediately has access
          // to free trial features.
          await this.$store.dispatch('auth/refresh')
          await this.$store.dispatch('saasApp/setShowFreeTrialStep', true)
        } catch (error) {
          this.installing = false
          this.handleError(error, 'workspace')
          return
        }
      }

      // Now that we have the workspace id, we can install the template into the provided
      // or newly created workspace.
      try {
        const { data } = await TemplateService(this.$client).install(
          workspaceId,
          template.id
        )
        // Installing a template has just created a couple of applications in the
        // workspace. The response contains those applications and we can add them to the
        // store so that the user can view the installed template right away.
        data.forEach((application) => {
          this.$store.dispatch('application/forceCreate', application)
        })
        if (data.length > 0) {
          // If there are applications, we want to select the first one right away.
          const application = this.$store.getters['application/get'](data[0].id)
          const type = this.$registry.get('application', application.type)
          type.select(application, this)
        }
      } catch (error) {
        this.installing = false
        this.handleError(error, 'template')
      }
    },
  },
}
</script>
